import React, { useMemo, useCallback } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  useTheme
} from "@mui/material";
import { Controller, Control, UseFormSetValue } from "react-hook-form";
import { useMediaQuery } from "@mui/material";
import {
  useRwandaLocation,
  useKivunovaTranslation,
  CountryFullName,
  Address
} from "@kivunova/kivufrontendcommon";

interface AddressFormValues {
  address: Address;
}

interface AddressFormProps {
  control: Control<AddressFormValues>;
  defaultAddress?: Address;
  setValue: UseFormSetValue<AddressFormValues>;
}

const AddressForm: React.FC<AddressFormProps> = ({
  control,
  defaultAddress,
  setValue
}) => {
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();

  const {
    getProvinces,
    getDistrictByProvince,
    getSectors,
    getCells,
    getVillages,
    loading: locationLoading
  } = useRwandaLocation();

  const [province, setProvince] = React.useState(
    defaultAddress?.province || ""
  );
  const [district, setDistrict] = React.useState(
    defaultAddress?.district || ""
  );
  const [sector, setSector] = React.useState(defaultAddress?.sector || "");
  const [cell, setCell] = React.useState(defaultAddress?.cell || "");
  const [village, setVillage] = React.useState(defaultAddress?.village || "");

  const provinces = useMemo(() => getProvinces(), [getProvinces]);
  const districts = useMemo(
    () => (province ? getDistrictByProvince(province) : []),
    [province, getDistrictByProvince]
  );
  const sectors = useMemo(
    () => (province && district ? getSectors(province, district) : []),
    [province, district, getSectors]
  );
  const cells = useMemo(
    () =>
      province && district && sector
        ? getCells(province, district, sector)
        : [],
    [province, district, sector, getCells]
  );
  const villages = useMemo(
    () =>
      province && district && sector && cell
        ? getVillages(province, district, sector, cell)
        : [],
    [province, district, sector, cell, getVillages]
  );

  const handleProvinceChange = useCallback(
    (val: string, onChange: (val: string) => void) => {
      setProvince(val);
      setDistrict("");
      setSector("");
      setCell("");
      setVillage("");
      setValue("address.district", "");
      setValue("address.sector", "");
      setValue("address.cell", "");
      setValue("address.village", "");
      onChange(val);
    },
    [setValue]
  );

  const handleDistrictChange = useCallback(
    (val: string, onChange: (val: string) => void) => {
      setDistrict(val);
      setSector("");
      setCell("");
      setVillage("");
      setValue("address.sector", "");
      setValue("address.cell", "");
      setValue("address.village", "");
      onChange(val);
    },
    [setValue]
  );

  const handleSectorChange = useCallback(
    (val: string, onChange: (val: string) => void) => {
      setSector(val);
      setCell("");
      setVillage("");
      setValue("address.cell", "");
      setValue("address.village", "");
      onChange(val);
    },
    [setValue]
  );

  const handleCellChange = useCallback(
    (val: string, onChange: (val: string) => void) => {
      setCell(val);
      setVillage("");
      setValue("address.village", "");
      onChange(val);
    },
    [setValue]
  );

  // Initialize local state from defaultAddress when it changes
  React.useEffect(() => {
    if (defaultAddress) {
      setProvince(defaultAddress.province || "");
      setDistrict(defaultAddress.district || "");
      setSector(defaultAddress.sector || "");
      setCell(defaultAddress.cell || "");
      setVillage(defaultAddress.village || "");
    }
  }, [defaultAddress]);

  return (
    <Box display="flex" flexDirection="column" gap={matchMD ? 1.5 : 2}>
      <Controller
        name="address.country"
        control={control}
        defaultValue={defaultAddress?.country || CountryFullName.RW}
        render={({ field }) => (
          <FormControl fullWidth size="small" disabled>
            <InputLabel>{t("country", "Country")}</InputLabel>
            <Select value={field.value} label={t("country", "Country")}>
              {Object.values(CountryFullName).map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <Controller
        name="address.province"
        control={control}
        defaultValue={defaultAddress?.province || ""}
        render={({ field }) => (
          <FormControl fullWidth size="small">
            <InputLabel>{t("province", "Province")}</InputLabel>
            <Select
              value={field.value || province}
              onChange={(e) => {
                const val = e.target.value;
                handleProvinceChange(val, field.onChange);
              }}
              disabled={locationLoading}
              label={t("province", "Province")}
              MenuProps={{ container: document.body }}
            >
              {provinces.map((p) => (
                <MenuItem key={p} value={p}>
                  {t(`location-province-${p}`, p)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {province && (
        <Controller
          name="address.district"
          control={control}
          defaultValue={defaultAddress?.district || ""}
          render={({ field }) => (
            <FormControl fullWidth size="small">
              <InputLabel>{t("district", "District")}</InputLabel>
              <Select
                value={field.value || district}
                onChange={(e) => {
                  const val = e.target.value;
                  handleDistrictChange(val, field.onChange);
                }}
                label={t("district", "District")}
                MenuProps={{ container: document.body }}
              >
                {districts.map((d) => (
                  <MenuItem key={d} value={d}>
                    {t(`location-district-${d}`, d)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )}

      {district && (
        <Controller
          name="address.sector"
          control={control}
          defaultValue={defaultAddress?.sector || ""}
          render={({ field }) => (
            <FormControl fullWidth size="small">
              <InputLabel>{t("sector", "Sector")}</InputLabel>
              <Select
                value={field.value || sector}
                onChange={(e) => {
                  const val = e.target.value;
                  handleSectorChange(val, field.onChange);
                }}
                label={t("sector", "Sector")}
                MenuProps={{ container: document.body }}
              >
                {sectors.map((s) => (
                  <MenuItem key={s} value={s}>
                    {t(`location-sector-${s}`, s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )}

      {sector && (
        <Controller
          name="address.cell"
          control={control}
          defaultValue={defaultAddress?.cell || ""}
          render={({ field }) => (
            <FormControl fullWidth size="small">
              <InputLabel>{t("cell", "Cell")}</InputLabel>
              <Select
                value={field.value || cell}
                onChange={(e) => {
                  const val = e.target.value;
                  handleCellChange(val, field.onChange);
                }}
                label={t("cell", "Cell")}
                MenuProps={{ container: document.body }}
              >
                {cells.map((c) => (
                  <MenuItem key={c} value={c}>
                    {t(`location-cell-${c}`, c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )}

      {cell && (
        <Controller
          name="address.village"
          control={control}
          defaultValue={defaultAddress?.village || ""}
          render={({ field }) => (
            <FormControl fullWidth size="small">
              <InputLabel>{t("village", "Village")}</InputLabel>
              <Select
                value={field.value || village}
                onChange={field.onChange}
                label={t("village", "Village")}
                MenuProps={{ container: document.body }}
              >
                {villages.map((v) => (
                  <MenuItem key={v} value={v}>
                    {t(`location-village-${v}`, v)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      )}

      <Controller
        name="address.streetAddress"
        control={control}
        defaultValue={defaultAddress?.streetAddress || ""}
        render={({ field }) => (
          <TextField
            {...field}
            label={t("street-address", "Street Address")}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "primary.main"
                }
              }
            }}
          />
        )}
      />
    </Box>
  );
};

export default AddressForm;
